const BASE = accountId => `/api/v1/accounts/${accountId}/power_data`;

// `fetch` so rejeita quando a rede falha: 4xx e 5xx chegam aqui como resposta
// normal, e `.then(json)` entregava o CORPO DO ERRO como se fosse dado.
// Medido em 09/09: no 502 do Rails (`{error: 'Knowledge base service is
// unavailable'}`) o `sources.value = data?.sources || data || []` do
// DatasourcesTab guardava esse objeto, `hasProcessing` chamava `.some` nele,
// estourava TypeError dentro do agendador e o poll MORRIA — a tela so voltava
// com F5, mostrando "nenhuma source ainda" numa base com 1.186. Um status que
// nao e lido vira dado corrompido em silencio.
const json = async response => {
  if (!response.ok) {
    let corpo = null;
    try {
      corpo = await response.clone().json();
    } catch {
      corpo = null;
    }
    const detalhe = corpo?.error || '';
    const erro = new Error(
      `${response.status} ${response.statusText}${detalhe ? ` — ${detalhe}` : ''}`
    );
    // A tela precisa do status para escolher a frase: 504 da página única é "demorou,
    // confira a lista", e não a falha genérica.
    erro.status = response.status;
    // E do código, quando o 422 tem nome: `url_interna` é o endereço recusado por apontar
    // para dentro da nossa rede, e pedir para "tentar de novo" seria a frase errada.
    // ACESSO-01 (L8a): a recusa do acesso nomeia o código em `codigo` (`escopo_ausente`,
    // `concessao_recusada`) e traz a frase em `error`, que fica na mensagem: copiada para o
    // `codigo`, ela virava um código que nenhuma tela conhece.
    erro.codigo = corpo?.codigo || (typeof detalhe === 'string' ? detalhe : '');
    // ACESSO-01 (L8b): a resposta na forma do axios (`response.status`, `response.data`), para o `fraseDaRecusa` ler a
    // recusa do acesso daqui também, e a tela mostrar a frase do servidor no lugar da frase genérica dela.
    erro.response = { status: response.status, data: corpo };
    throw erro;
  }
  return response.json();
};

// DELETE não devolve corpo, mas o status continua importando: sem conferir, um 401 ou um 502
// virava "Base excluída" na tela, e a base continuava lá.
const semCorpo = async response => {
  if (!response.ok) await json(response);
  return response;
};

export default {
  index(accountId) {
    return fetch(BASE(accountId)).then(json);
  },
  create(accountId, data) {
    return fetch(BASE(accountId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(json);
  },
  show(accountId, id) {
    return fetch(`${BASE(accountId)}/${id}`).then(json);
  },
  update(accountId, id, data) {
    return fetch(`${BASE(accountId)}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(json);
  },
  destroy(accountId, id) {
    return fetch(`${BASE(accountId)}/${id}`, { method: 'DELETE' }).then(
      semCorpo
    );
  },
  sourcesIndex(accountId, id) {
    return fetch(`${BASE(accountId)}/${id}/sources`).then(json);
  },
  sourcesShow(accountId, id, sourceId) {
    return fetch(`${BASE(accountId)}/${id}/sources/${sourceId}`).then(json);
  },
  sourcesCreate(accountId, id, formData) {
    return fetch(`${BASE(accountId)}/${id}/sources`, {
      method: 'POST',
      body: formData, // multipart
    }).then(json);
  },
  sourcesUpdate(accountId, id, sourceId, data) {
    return fetch(`${BASE(accountId)}/${id}/sources/${sourceId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(json);
  },
  sourcesSuggestTags(accountId, id, sourceId) {
    return fetch(`${BASE(accountId)}/${id}/sources/${sourceId}/suggest-tags`, {
      method: 'POST',
    }).then(json);
  },
  // Tags sugeridas para texto que ainda nao e source (o dialogo de adicionar).
  suggestTagsFromText(accountId, id, text) {
    return fetch(`${BASE(accountId)}/${id}/suggest-tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    }).then(json);
  },
  // Pares de Q&A desmontados, para montar o CSV aqui no navegador.
  sourcesQaIndex(accountId, id) {
    return fetch(`${BASE(accountId)}/${id}/sources/qa`).then(json);
  },
  sourcesDestroy(accountId, id, sourceId) {
    return fetch(`${BASE(accountId)}/${id}/sources/${sourceId}`, {
      method: 'DELETE',
    }).then(semCorpo);
  },
  // PD-02: áudio e vídeo, transcritos no servidor. Mesmo multipart do `sourcesCreate`.
  sourcesCreateMidia(accountId, id, formData) {
    return fetch(`${BASE(accountId)}/${id}/sources/midia`, {
      method: 'POST',
      body: formData,
    }).then(json);
  },
  // "Tentar de novo" de uma mídia em erro: transcreve o mesmo original guardado.
  sourcesTranscrever(accountId, id, sourceId) {
    return fetch(`${BASE(accountId)}/${id}/sources/${sourceId}/transcrever`, {
      method: 'POST',
    }).then(json);
  },
  sourcesReprocess(accountId, id, sourceId) {
    return fetch(`${BASE(accountId)}/${id}/sources/${sourceId}/reprocess`, {
      method: 'POST',
    }).then(json);
  },
  connectionsIndex(accountId, id) {
    return fetch(`${BASE(accountId)}/${id}/connections`).then(json);
  },
  connectionsCreate(accountId, id, agentId) {
    return fetch(`${BASE(accountId)}/${id}/connections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent_id: agentId }),
    }).then(json);
  },
  connectionsDestroy(accountId, id, agentId) {
    return fetch(`${BASE(accountId)}/${id}/connections/${agentId}`, {
      method: 'DELETE',
    });
  },
  // PWRDATA-01: every chatbot on the account, PowerAgents and PowerAgents alike,
  // normalized onto { id, name, kind, status } so one picker can list both.
  chatbots(accountId) {
    return fetch(`${BASE(accountId)}/chatbots`).then(json);
  },
  search(accountId, id, { agentId, query, topK = 5 }) {
    return fetch(`${BASE(accountId)}/${id}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent_id: agentId, query, top_k: topK }),
    }).then(json);
  },
  agentStores(accountId, agentId) {
    return fetch(`${BASE(accountId)}/agent/${agentId}/stores`).then(json);
  },
  sourcesScrape(accountId, id, url, opcoes = null) {
    return fetch(`${BASE(accountId)}/${id}/sources/scrape`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, opcoes }),
    }).then(json);
  },
  matchTemplate(url) {
    return fetch(
      `/api/v1/power_data/templates/match?url=${encodeURIComponent(url)}`
    ).then(json);
  },
  // Dispara a descoberta e devolve { discovery_id, status } na hora. A lista de
  // paginas NAO vem aqui: mapear um site leva dezenas de segundos e nao cabe no
  // teto do Rails (8s) nem no do Rack (15s). Quem espera e o poll abaixo.
  sourcesSiteDiscover(accountId, id, url, limit = 300, sitemap = false) {
    return fetch(`${BASE(accountId)}/${id}/sources/site/discover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, limit, sitemap }),
    }).then(json);
  },
  sourcesSiteDiscoverStatus(accountId, id, discoveryId) {
    return fetch(
      `${BASE(accountId)}/${id}/sources/site/discover/${discoveryId}`
    ).then(json);
  },
  sourcesScrapeSite(
    accountId,
    id,
    { url, urls, tags = [], concurrency = 4, opcoes = null }
  ) {
    return fetch(`${BASE(accountId)}/${id}/sources/scrape-site`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, urls, tags, concurrency, opcoes }),
    }).then(json);
  },
};
